import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Menu, X as XIcon, Sparkles, ArrowRight } from 'lucide-react';
import { toast } from 'sonner';

const C = { dark: '#0F0F0F', accent: '#FF2E00', bg: '#FAFAFA' };
const API = process.env.REACT_APP_BACKEND_URL;

/** Persistent header nav for every HuscleOS public page. */
export default function HuscleosHeader({ variant = 'auto' }) {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [showContact, setShowContact] = useState(false);
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const onHome = pathname === '/';

  useEffect(() => {
    const fn = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', fn, { passive: true });
    return () => window.removeEventListener('scroll', fn);
  }, []);

  useEffect(() => { setOpen(false); }, [pathname]);

  // On-home = transparent over hero, solid on scroll. Off-home = always solid.
  const solid = variant === 'solid' ? true : (!onHome || scrolled);

  const jumpHome = (hash) => {
    setOpen(false);
    if (onHome) document.getElementById(hash)?.scrollIntoView({ behavior: 'smooth' });
    else navigate(`/#${hash}`);
  };

  const buildMyDemo = () => {
    setOpen(false);
    if (onHome) document.getElementById('quiz')?.scrollIntoView({ behavior: 'smooth' });
    else navigate('/#quiz');
  };

  const sectionLinks = [
    { label: 'How It Works', hash: 'pillars' },
    { label: 'Results',      hash: 'outcomes' },
    { label: 'Templates',    hash: 'templates' },
    { label: 'Pricing',      hash: 'pricing' },
  ];
  const routedLinks = [
    { label: 'Blog',  to: '/blog' },
    { label: 'Merch', to: '/merch' },
  ];

  // Base text color based on solid vs transparent state
  const linkBase = solid ? 'text-gray-700 hover:text-gray-900' : 'text-white/85 hover:text-white';
  const logoGray = solid ? C.dark : '#fff';

  return (
    <>
      <nav
        data-testid="huscleos-header"
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${solid ? 'bg-white/95 backdrop-blur-xl border-b border-gray-200/80' : 'bg-transparent'} ${solid ? 'shadow-[0_1px_3px_rgba(0,0,0,0.04)]' : ''}`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between h-[72px]">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-0 select-none shrink-0 group" data-testid="header-logo">
            <span style={{ fontFamily: 'Bebas Neue', fontSize: '1.6rem', letterSpacing: '0.5px', color: logoGray, lineHeight: 1 }} className="transition-colors">HUSCLE</span>
            <span style={{ fontFamily: 'Bebas Neue', fontSize: '1.6rem', letterSpacing: '0.5px', color: C.accent, lineHeight: 1 }}>OS</span>
          </Link>

          {/* Desktop links */}
          <div className="hidden lg:flex items-center gap-1 absolute left-1/2 -translate-x-1/2">
            {sectionLinks.map((l) => (
              <button
                key={l.label}
                onClick={() => jumpHome(l.hash)}
                className={`text-[13px] font-semibold tracking-wide px-3 py-2 transition-colors rounded ${linkBase}`}
                style={{ fontFamily: 'Manrope' }}
                data-testid={`header-link-${l.label.toLowerCase().replace(/\s+/g, '-')}`}
              >
                {l.label}
              </button>
            ))}
            {routedLinks.map((l) => (
              <Link
                key={l.label}
                to={l.to}
                className={`text-[13px] font-semibold tracking-wide px-3 py-2 transition-colors rounded ${linkBase}`}
                style={{ fontFamily: 'Manrope' }}
                data-testid={`header-link-${l.label.toLowerCase()}`}
              >
                {l.label}
              </Link>
            ))}
          </div>

          {/* Right cluster */}
          <div className="hidden lg:flex items-center gap-2 shrink-0">
            <button
              onClick={() => setShowContact(true)}
              className={`text-[13px] font-semibold tracking-wide px-3 py-2 transition-colors ${linkBase}`}
              style={{ fontFamily: 'Manrope' }}
              data-testid="header-contact-btn"
            >Let's Talk</button>
            <button
              onClick={buildMyDemo}
              data-testid="header-build-demo"
              className="group flex items-center gap-1.5 text-xs font-bold tracking-widest uppercase px-5 py-2.5 ml-1 hover:opacity-95 transition-all shadow-sm hover:shadow-md"
              style={{ fontFamily: 'Manrope', background: C.accent, color: '#fff' }}
            >
              Build My Demo <ArrowRight size={13} className="transition-transform group-hover:translate-x-0.5" />
            </button>
          </div>

          {/* Mobile: Build Demo (prominent) + hamburger */}
          <div className="lg:hidden flex items-center gap-2">
            <button
              onClick={buildMyDemo}
              data-testid="header-build-demo-mobile"
              className="text-[11px] font-bold tracking-widest uppercase px-3.5 py-2 hover:opacity-95"
              style={{ fontFamily: 'Manrope', background: C.accent, color: '#fff' }}
            >
              Build Demo
            </button>
            <button
              onClick={() => setOpen(!open)}
              aria-label={open ? 'Close menu' : 'Open menu'}
              data-testid="header-hamburger"
              className={`p-2 -mr-2 transition-colors ${solid ? 'text-gray-900' : 'text-white'}`}
            >
              {open ? <XIcon size={22} /> : <Menu size={22} />}
            </button>
          </div>
        </div>

        {/* Mobile drawer */}
        {open && (
          <div className="lg:hidden bg-white border-t border-gray-200 shadow-lg animate-[fadeInDown_0.2s_ease-out]" data-testid="header-mobile-menu">
            <div className="px-4 py-4 space-y-1">
              {sectionLinks.map((l) => (
                <button
                  key={l.label}
                  onClick={() => jumpHome(l.hash)}
                  className="block w-full text-left text-base font-semibold py-3 px-2 text-gray-800 hover:bg-gray-50 transition-colors"
                  style={{ fontFamily: 'Manrope' }}
                  data-testid={`mobile-link-${l.label.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  {l.label}
                </button>
              ))}
              {routedLinks.map((l) => (
                <Link
                  key={l.label}
                  to={l.to}
                  className="block w-full text-left text-base font-semibold py-3 px-2 text-gray-800 hover:bg-gray-50 transition-colors"
                  style={{ fontFamily: 'Manrope' }}
                  data-testid={`mobile-link-${l.label.toLowerCase()}`}
                >
                  {l.label}
                </Link>
              ))}
              <button
                onClick={() => { setOpen(false); setShowContact(true); }}
                className="block w-full text-left text-base font-semibold py-3 px-2 text-gray-800 hover:bg-gray-50 transition-colors"
                style={{ fontFamily: 'Manrope' }}
                data-testid="mobile-link-contact"
              >
                Let's Talk
              </button>
              <button
                onClick={buildMyDemo}
                className="w-full text-center text-sm font-bold tracking-widest uppercase px-6 py-3.5 mt-3"
                style={{ fontFamily: 'Manrope', background: C.accent, color: '#fff' }}
              >
                Build My Demo
              </button>
            </div>
          </div>
        )}
      </nav>

      {showContact && <ContactModal onClose={() => setShowContact(false)} />}
    </>
  );
}

/** Contact form modal — posts to /api/huscleos/contact with duplicate detection. */
function ContactModal({ onClose }) {
  const [f, setF] = useState({ name: '', email: '', phone: '', gym_name: '', message: '' });
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    if (!f.name || !f.email || !f.message) return;
    setSending(true);
    try {
      const res = await fetch(`${API}/api/huscleos/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(f),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'failed');
      setResult(data);
      toast.success(data.message || "Thanks — we'll be in touch.");
    } catch (err) {
      toast.error('Something went wrong. Please try again.');
    } finally {
      setSending(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center px-4 py-10 bg-black/60 backdrop-blur-sm overflow-y-auto"
      onClick={onClose}
      data-testid="contact-modal"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-lg bg-white shadow-2xl relative my-auto"
        style={{ fontFamily: 'Manrope, sans-serif' }}
      >
        <button
          onClick={onClose}
          data-testid="contact-close"
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-900 p-1"
          aria-label="Close"
        >
          <XIcon size={20} />
        </button>
        <div className="p-8 sm:p-10">
          {!result ? (
            <>
              <div className="inline-flex items-center gap-2 px-3 py-1 bg-[#FFEDE8] mb-5">
                <Sparkles size={12} className="text-[#FF2E00]" />
                <span className="text-[10px] font-bold tracking-widest uppercase text-[#FF2E00]">Let's Talk</span>
              </div>
              <h2 className="text-3xl font-bold tracking-tight mb-2" style={{ fontFamily: 'Bebas Neue', letterSpacing: '1px', color: '#0F0F0F' }}>
                HOW CAN WE HELP?
              </h2>
              <p className="text-sm text-gray-600 mb-6">Drop us a note — we usually reply within a few hours.</p>
              <form onSubmit={submit} className="space-y-3" data-testid="contact-form">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <input required type="text" placeholder="Your name *" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })}
                    className="w-full px-3 py-2.5 text-sm bg-gray-50 border border-gray-200 focus:outline-none focus:border-gray-900 transition-colors"
                    data-testid="contact-name" />
                  <input type="text" placeholder="Gym name (optional)" value={f.gym_name} onChange={(e) => setF({ ...f, gym_name: e.target.value })}
                    className="w-full px-3 py-2.5 text-sm bg-gray-50 border border-gray-200 focus:outline-none focus:border-gray-900 transition-colors"
                    data-testid="contact-gym" />
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <input required type="email" placeholder="Email *" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })}
                    className="w-full px-3 py-2.5 text-sm bg-gray-50 border border-gray-200 focus:outline-none focus:border-gray-900 transition-colors"
                    data-testid="contact-email" />
                  <input type="tel" placeholder="Phone (optional)" value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })}
                    className="w-full px-3 py-2.5 text-sm bg-gray-50 border border-gray-200 focus:outline-none focus:border-gray-900 transition-colors"
                    data-testid="contact-phone" />
                </div>
                <textarea required placeholder="What are you trying to figure out? *" value={f.message} onChange={(e) => setF({ ...f, message: e.target.value })} rows={5}
                  className="w-full px-3 py-2.5 text-sm bg-gray-50 border border-gray-200 focus:outline-none focus:border-gray-900 transition-colors resize-none"
                  data-testid="contact-message" />
                <button type="submit" disabled={sending} data-testid="contact-submit"
                  className="w-full py-3.5 text-sm font-bold tracking-widest uppercase text-white bg-[#FF2E00] hover:opacity-90 transition-all disabled:opacity-50"
                  style={{ fontFamily: 'Manrope' }}>
                  {sending ? 'SENDING...' : 'SEND MESSAGE'}
                </button>
                <p className="text-[10px] text-gray-500 text-center mt-2">No spam. Just a real reply from the HuscleOS team.</p>
              </form>
            </>
          ) : (
            <div className="text-center py-4" data-testid="contact-result">
              <div className="inline-flex items-center justify-center w-12 h-12 bg-green-100 rounded-full mb-4">
                <Sparkles size={20} className="text-green-600" />
              </div>
              <h2 className="text-2xl font-bold mb-3" style={{ fontFamily: 'Bebas Neue', letterSpacing: '1px', color: '#0F0F0F' }}>
                {result.status === 'already_exists' ? 'WE SEE YOU.' : 'GOT IT.'}
              </h2>
              <p className="text-sm text-gray-700 mb-4">{result.message}</p>
              {result.has_existing_demo && (
                <p className="text-sm text-gray-600 mb-6 p-3 bg-gray-50 border border-gray-200">
                  You already have a HuscleOS demo environment — check your inbox for the login, or we can resend it.
                </p>
              )}
              <button onClick={onClose} data-testid="contact-close-success"
                className="px-8 py-3 text-sm font-bold tracking-widest uppercase text-white bg-gray-900 hover:bg-black transition-colors"
                style={{ fontFamily: 'Manrope' }}>
                GOT IT
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
